/**
 * Single source of truth for everything shown on the site.
 *
 * Populated from CV-2026-edited.pdf. This is the only file you edit to
 * change site content — every component reads from here.
 *
 * Notes on a couple of judgment calls (tweak freely):
 *   - The phone number from the CV is intentionally NOT published here
 *     (a public website doesn't need your mobile). Add it back if you want.
 *   - The Skills section isn't on the CV verbatim — it's derived from the
 *     technologies your experience describes. Adjust to taste.
 */

export interface ExperienceItem {
  role: string;
  company: string;
  period: string;
  location?: string;
  summary?: string;
  highlights: string[];
}

export interface EducationItem {
  credential: string;
  institution: string;
  period: string;
  detail?: string;
}

export interface ProjectItem {
  name: string;
  blurb: string;
  link?: string;
  tags?: string[];
  /** Optional role/credit line, e.g. "Creator & sole engineer". */
  role?: string;
  /** Set true to render as the large flagship project block. */
  featured?: boolean;
}

export interface SkillGroup {
  label: string;
  items: string[];
}

export interface SiteContent {
  name: string;
  tagline: string;
  /** Short pitch shown in the hero, 1–2 sentences. */
  intro: string;
  location: string;
  email: string;
  /** Path (in /public) to the downloadable CV PDF, or null to hide the button. */
  cvFile: string | null;
  links: { label: string; href: string }[];
  /** Swiss "spec sheet" data row shown under the hero. */
  specs: { label: string; value: string }[];
  about: string[];
  experience: ExperienceItem[];
  skills: SkillGroup[];
  projects: ProjectItem[];
  education: EducationItem[];
}

export const content: SiteContent = {
  name: "Laurel Daigneault",
  tagline: "Full-Stack Software Engineer & Manager",
  intro:
    "A decade of turning ideas into scalable, robust systems — across frontend, backend, infrastructure, and AI — with a hands-on, builder's approach to leadership.",
  location: "Remote",
  email: "laurel2195@gmail.com",
  cvFile: "/laurel-daigneault-cv.pdf",
  links: [
    { label: "LinkedIn", href: "https://www.linkedin.com/in/laurel-daigneault" },
    { label: "GitHub", href: "https://github.com/laureldaigneault" },
    { label: "Email", href: "mailto:laurel2195@gmail.com" },
  ],

  specs: [
    { label: "Experience", value: "10+ years" },
    { label: "Discipline", value: "Full-stack + AI" },
    { label: "Current", value: "Eng. Manager / Tech Lead" },
    { label: "Based", value: "Remote" },
  ],

  about: [
    "Full-stack software engineer and hands-on manager with over a decade of experience crafting complex software solutions, websites, and apps. As a startup veteran, I work across the whole spectrum — from frontend and backend to infrastructure and AI integration.",
    "My strength is turning ideas into scalable, robust systems, paired with a keen eye for design and user experience. As an empathetic leader with an entrepreneurial spirit, I build teamwork, spark innovation, and contribute to the broader vision of every project I take on.",
  ],

  experience: [
    {
      role: "Engineering Manager / Tech Lead",
      company: "Zeel Networks",
      period: "Jan 2018 — Present",
      location: "Remote",
      highlights: [
        "Lead front-end engineering teams across web & mobile while staying hands-on with the code, in a fully remote environment.",
        "Coordinate cross-team efforts, resolve challenges, and ensure we meet requirements and ship a scalable, maintainable, quality product.",
        "Built Zeel's website, internal CMS, and CI/CD tooling from the ground up, with scalability and maintainability as first principles.",
      ],
    },
    {
      role: "Independent Software Engineering Consultant",
      company: "Self-employed",
      period: "Since 2020",
      highlights: [
        "Built and launched websites & systems for startups.",
        "Set up technical foundations aligned with each business's vision.",
        "Led refactoring initiatives for scalability and optimized performance.",
      ],
    },
    {
      role: "Full-Stack Engineer",
      company: "Commure",
      period: "2017 — 2018",
      highlights: [
        "Developed a React Native mobile application.",
        "Built a React web application with offline capabilities from the ground up.",
      ],
    },
    {
      role: "Full-Stack Cloud Developer",
      company: "Enkidoo",
      period: "2016 — 2017",
      highlights: [
        "Worked within the development team to build cloud-based business solutions.",
        "Participated in research & development projects involving Machine Learning.",
      ],
    },
    {
      role: "Software Engineer Intern",
      company: "Canadian Space Agency",
      period: "Jan 2016 — Apr 2016",
      highlights: [
        "Developed a web junction platform.",
        "Wrote and executed test procedures.",
      ],
    },
  ],

  skills: [
    {
      label: "Frontend",
      items: ["React", "React Native", "TypeScript", "JavaScript", "UI / UX"],
    },
    {
      label: "Backend & Infra",
      items: ["Node.js", "REST APIs", "CMS", "CI/CD", "Cloud"],
    },
    {
      label: "Platform & AI",
      items: ["Tauri (desktop)", "AI integration", "Machine Learning"],
    },
    {
      label: "Leadership",
      items: ["Team management", "Tech leadership", "Mentoring", "Remote teams"],
    },
  ],

  projects: [
    {
      name: "Atlas Desk",
      role: "Creator & sole engineer",
      featured: true,
      blurb:
        "A cross-platform desktop suite built end-to-end: a Tauri + React “fake-OS” shell that hosts a dozen self-contained apps, each independently bundled and installed on-demand from a Cloudflare registry. It's backed by a versioned host API with crash recovery and automatic rollback, a signed auto-updater, and per-app local-first storage. The result is software that feels understandable again — a quiet, lightweight desktop of focused tools that run offline and stay out of your way.",
      link: "https://atlasdesk.co",
      tags: [
        "Tauri",
        "React",
        "TypeScript",
        "Rust",
        "Cloudflare R2 / Pages",
        "Local-first",
        "Auto-update",
      ],
    },
    {
      name: "Telepromptr",
      blurb:
        "A credit-based web SaaS for building model-agnostic AI prompt chains, operable through webhooks. Set up workflow inputs, compose prompt sequences, and run your workflows — designed to make AI integration and automation simple.",
      tags: ["SaaS", "AI", "Automation"],
    },
  ],

  education: [
    {
      credential: "Baccalaureate in Software Engineering",
      institution: "École de technologie supérieure (ÉTS)",
      period: "Montreal, Canada",
      detail:
        "Continuously expanded my technical skill set beyond formal education through self-directed learning, keeping pace with the latest industry trends and technologies.",
    },
  ],
};
